import { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { AddressAutocomplete } from '../components/AddressAutocomplete';
import { toast } from 'sonner';
import { Eye, EyeOff, ArrowLeft, Store, Gift } from 'lucide-react';
import axios from 'axios';
import { getApiErrorMessage } from '../utils/errorHelper';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function RestaurantSignup() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { registerRestaurant } = useAuth();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [referralCode, setReferralCode] = useState(searchParams.get('ref') || '');
  const [referralValid, setReferralValid] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
    restaurant_name: '',
    address: '',
    city: '',
    zipcode: '',
    latitude: null,
    longitude: null
  });

  const handleAddressSelect = (addressData) => {
    setFormData(prev => ({
      ...prev,
      address: addressData.address || addressData.formatted_address,
      city: addressData.city,
      zipcode: addressData.zipcode,
      latitude: addressData.latitude,
      longitude: addressData.longitude
    }));
  };

  // Validate referral code from URL
  useEffect(() => {
    const code = searchParams.get('ref');
    if (!code) return;
    axios.get(`${API}/referral/validate/${code}`)
      .then(res => {
        if (res.data.valid) {
          setReferralCode(code.toUpperCase());
          setReferralValid(res.data);
        }
      })
      .catch(() => setReferralValid(null));
  }, [searchParams]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.password !== formData.confirmPassword) {
      toast.error('Les mots de passe ne correspondent pas');
      return;
    }
    
    if (!acceptTerms) {
      toast.error('Veuillez accepter les conditions');
      return;
    }

    if (formData.password.length < 8) {
      toast.error('Le mot de passe doit contenir au moins 8 caractères');
      return;
    }
    
    setLoading(true);
    
    try {
      await registerRestaurant({
        name: formData.name,
        email: formData.email,
        phone: formData.phone || null,
        password: formData.password,
        restaurant_name: formData.restaurant_name,
        address: formData.address,
        city: formData.city,
        zipcode: formData.zipcode,
        referral_code: referralCode || null
      });
      toast.success('Restaurant inscrit avec succès!');
      navigate('/restaurant-dashboard/settings');
    } catch (_error) {
      toast.error(getApiErrorMessage(error, "Erreur lors de l'inscription"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Header */}
      <header className="bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2">
              <img src="/logo.png" alt="LEMO" className="w-20 h-20 -my-4 object-contain" />
              <span className="font-bold text-xl text-gray-900">LEMO</span>
            </Link>
            <Link to="/" className="text-gray-500 hover:text-gray-700 flex items-center gap-2">
              <ArrowLeft className="w-4 h-4" />
              Retour
            </Link>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 flex items-center justify-center p-4 py-8">
        <div className="w-full max-w-xl">
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8">
            <div className="text-center mb-8">
              <div className="w-16 h-16 rounded-2xl bg-emerald-100 flex items-center justify-center mx-auto mb-4">
                <Store className="w-8 h-8 text-emerald-600" />
              </div>
              <h1 className="text-2xl font-bold text-gray-900">Inscription Restaurant</h1>
              <p className="text-gray-500 mt-2">Rejoignez LEMO et développez votre activité</p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5">
              {/* Referral Banner */}
              {referralCode && referralValid && (
                <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-xl flex items-center gap-3" data-testid="referral-signup-banner">
                  <Gift className="w-5 h-5 text-emerald-600 shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-emerald-800">
                      Parraine par <strong>{referralValid.referrer_name}</strong>
                    </p>
                    <p className="text-xs text-emerald-600">{referralValid.benefit}</p>
                  </div>
                </div>
              )}
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="name">Votre nom</Label>
                  <Input
                    id="name"
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Jean Dupont"
                    className="mt-1.5 h-12 rounded-xl"
                    required
                    data-testid="restaurant-signup-name"
                  />
                </div>
                <div>
                  <Label htmlFor="phone">Téléphone</Label>
                  <Input
                    id="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    placeholder="+33 6 12 34 56 78"
                    className="mt-1.5 h-12 rounded-xl"
                    data-testid="restaurant-signup-phone"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="email">Email professionnel</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="contact@restaurant.com"
                  className="mt-1.5 h-12 rounded-xl"
                  required
                  data-testid="restaurant-signup-email"
                />
              </div>

              <div className="border-t border-gray-100 pt-5">
                <h3 className="font-semibold text-gray-900 mb-4">Informations du restaurant</h3>
                
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="restaurant_name">Nom du restaurant</Label>
                    <Input
                      id="restaurant_name"
                      type="text"
                      value={formData.restaurant_name}
                      onChange={(e) => setFormData({ ...formData, restaurant_name: e.target.value })}
                      placeholder="Mon Restaurant"
                      className="mt-1.5 h-12 rounded-xl"
                      required
                      data-testid="restaurant-signup-restaurant-name"
                    />
                  </div>

                  <div>
                    <Label>Adresse (avec autocomplétion)</Label>
                    <div className="mt-1.5">
                      <AddressAutocomplete
                        value={formData.address}
                        onChange={(value) => setFormData({ ...formData, address: value })}
                        onSelect={handleAddressSelect}
                        placeholder="Commencez à taper votre adresse..."
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="city">Ville</Label>
                      <Input
                        id="city"
                        type="text"
                        value={formData.city}
                        onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                        placeholder="Paris"
                        className="mt-1.5 h-12 rounded-xl"
                        required
                        data-testid="restaurant-signup-city"
                      />
                    </div>
                    <div>
                      <Label htmlFor="zipcode">Code postal</Label>
                      <Input
                        id="zipcode"
                        type="text"
                        value={formData.zipcode}
                        onChange={(e) => setFormData({ ...formData, zipcode: e.target.value })}
                        placeholder="75001"
                        className="mt-1.5 h-12 rounded-xl"
                        required
                        data-testid="restaurant-signup-zipcode"
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="border-t border-gray-100 pt-5">
                <h3 className="font-semibold text-gray-900 mb-4">Sécurité</h3>
                
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="password">Mot de passe</Label>
                    <div className="relative mt-1.5">
                      <Input
                        id="password"
                        type={showPassword ? 'text' : 'password'}
                        value={formData.password}
                        onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                        placeholder="Minimum 8 caractères"
                        className="h-12 rounded-xl pr-12"
                        required
                        minLength={8}
                        data-testid="restaurant-signup-password"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      >
                        {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                      </button>
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="confirmPassword">Confirmer le mot de passe</Label>
                    <Input
                      id="confirmPassword"
                      type="password"
                      value={formData.confirmPassword}
                      onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                      placeholder="••••••••"
                      className="mt-1.5 h-12 rounded-xl"
                      required
                      data-testid="restaurant-signup-confirm-password"
                    />
                  </div>
                </div>
              </div>

              {/* Referral Code Field */}
              {!referralValid && (
                <div>
                  <Label htmlFor="referral_code">Code de parrainage (optionnel)</Label>
                  <Input
                    id="referral_code"
                    type="text"
                    value={referralCode}
                    onChange={(e) => setReferralCode(e.target.value.toUpperCase())}
                    placeholder="Ex: ABCD1234"
                    className="mt-1.5 h-12 rounded-xl tracking-widest"
                    data-testid="restaurant-signup-referral-code"
                  />
                </div>
              )}

              <div className="flex items-start gap-3">
                <Checkbox
                  id="terms"
                  checked={acceptTerms}
                  onCheckedChange={setAcceptTerms}
                  className="mt-1"
                />
                <label htmlFor="terms" className="text-sm text-gray-600 cursor-pointer">
                  J&apos;accepte les{' '}
                  <a href="#" className="text-emerald-600 hover:underline">conditions commerciales</a>
                  {' '}et la{' '}
                  <a href="#" className="text-emerald-600 hover:underline">politique de confidentialité</a>
                </label>
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full h-12 bg-emerald-500 hover:bg-emerald-600 rounded-full font-semibold"
                data-testid="restaurant-signup-submit"
              >
                {loading ? 'Inscription...' : 'Inscrire mon restaurant'}
              </Button>
            </form>

            <div className="mt-6 text-center">
              <p className="text-gray-500">
                Déjà partenaire?{' '}
                <Link to="/login" className="text-emerald-600 hover:text-emerald-700 font-medium">
                  Se connecter
                </Link>
              </p>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
